/**
 * Inline editing with autosave.
 *
 * "Data entry speed beats visual polish. Inline editing over
 * modal-per-field." A rep updating six deals before a Monday call should never
 * wait for a page load or hunt for a field.
 *
 * Autosave on blur, with a visible saved/saving indicator, plus Enter to
 * commit and Escape to abandon. §6.10 makes concurrency last-write-wins with a
 * toast rather than a conflict dialog, so there is nothing here to resolve —
 * the notice arrives from the mutation envelope.
 */

import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export type InlineKind = 'text' | 'date' | 'number' | 'select' | 'textarea';

export function InlineEdit({
  value,
  onSave,
  kind = 'text',
  options,
  placeholder = '—',
  disabled = false,
  label,
  className,
}: {
  value: string | null | undefined;
  onSave: (next: string | null) => Promise<unknown>;
  kind?: InlineKind;
  options?: { value: string; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  label: string;
  className?: string;
}): React.ReactElement {
  const [draft, setDraft] = useState(value ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  const commit = async (): Promise<void> => {
    const next = draft.trim() === '' ? null : draft;
    if ((value ?? '') === (next ?? '')) return;
    setState('saving');
    try {
      await onSave(next);
      setState('saved');
      window.setTimeout(() => setState('idle'), 1500);
    } catch {
      // The mutation layer has already toasted; put the field back.
      setDraft(value ?? '');
      setState('idle');
    }
  };

  const shared = {
    'aria-label': label,
    disabled,
    value: draft,
    onBlur: () => void commit(),
    onChange: (event: { target: { value: string } }) => setDraft(event.target.value),
  };

  return (
    <span className={cn('relative flex items-center gap-1', className)}>
      {kind === 'select' ? (
        <Select
          {...shared}
          className="h-8 text-sm"
          onChange={(event) => {
            setDraft(event.target.value);
            // A select has no meaningful "still typing" state.
            window.setTimeout(() => void commit(), 0);
          }}
        >
          <option value="">{placeholder}</option>
          {(options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      ) : kind === 'textarea' ? (
        <Textarea {...shared} rows={2} className="text-sm" placeholder={placeholder} />
      ) : (
        <Input
          {...shared}
          type={kind === 'date' ? 'date' : kind === 'number' ? 'number' : 'text'}
          className="h-8 text-sm"
          placeholder={placeholder}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
            if (event.key === 'Escape') setDraft(value ?? '');
          }}
        />
      )}

      <span className="w-4 shrink-0" aria-live="polite">
        {state === 'saving' && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label="Saving" />
        )}
        {state === 'saved' && <Check className="h-3.5 w-3.5 text-emerald-600" aria-label="Saved" />}
      </span>
    </span>
  );
}
